import React from "react";
import { Loader2 } from "lucide-react";
import PublicArticleTopicRow from "@/components/public/PublicArticleTopicRow";
import LearningArticleSearch from "@/components/public/LearningArticleSearch";
import LearningPathCards from "@/components/public/LearningPathCards";
import LearningProgressTools from "@/components/public/LearningProgressTools";
import LearningTopicFilters from "@/components/public/LearningTopicFilters";
import MiniTopicQuiz from "@/components/public/MiniTopicQuiz";
import { getLearningTopicCopy, rtlLearningLanguages } from "@/components/public/learningTopicTranslations";
import { getLearningArticleTitle } from "@/i18n";
import { localizedLearningArticleUrl } from "@/lib/learningUrls";

function classifyArticle(article) {
  const text = `${article.title || ""} ${article.subtopic || ""} ${article.excerpt || ""} ${article.meta_description || ""}`.toLowerCase();
  const difficulty = article.difficulty_level || (text.includes("advanced") || text.includes("offshore") || text.includes("yachtmaster") ? "advanced" : text.includes("exam") || text.includes("certification") ? "intermediate" : "beginner");
  const category = text.includes("license") || text.includes("licence") || text.includes("exam") || text.includes("certification") ? "license" : text.includes("safety") || text.includes("emergency") ? "safety" : text.includes("navigation") || text.includes("route") ? "navigation" : text.includes("weather") || text.includes("wind") ? "weather" : text.includes("regulation") || text.includes("rules") ? "regulations" : "topic";
  const takeaways = category === "license" ? ["exam rules", "study checklist", "common mistakes"] : category === "safety" ? ["safety steps", "crew checklist", "risk signals"] : category === "navigation" ? ["route basics", "planning checks", "skipper tips"] : category === "weather" ? ["forecast signals", "go/no-go checks", "weather risks"] : ["key concepts", "practical checklist", "common mistakes"];
  return { ...article, difficulty_level: difficulty, difficultyLabel: difficulty.charAt(0).toUpperCase() + difficulty.slice(1), category, takeaways };
}

export default function LearningTopicDetail({ topic, articles = [], isLoading = false, prefix = "/en", topicSlug = "", lang = "en" }) {
  const [search, setSearch] = React.useState("");
  const [activeFilter, setActiveFilter] = React.useState("all");
  const [completed, setCompleted] = React.useState([]);
  const [saved, setSaved] = React.useState([]);

  const copy = getLearningTopicCopy(lang).detail;
  const isRtl = rtlLearningLanguages.has(lang);
  const storageKey = `learning-topic:${lang}:${topicSlug}`;
  const enrichedArticles = articles.map(classifyArticle);

  React.useEffect(() => {
    const stored = JSON.parse(localStorage.getItem(storageKey) || "{}");
    setCompleted(stored.completed || []);
    setSaved(stored.saved || []);
  }, [storageKey]);

  React.useEffect(() => {
    localStorage.setItem(storageKey, JSON.stringify({ completed, saved }));
  }, [storageKey, completed, saved]);

  const normalizedSearch = search.trim().toLowerCase();
  const filteredArticles = enrichedArticles.filter((article) => {
    const matchesSearch = !normalizedSearch || `${getLearningArticleTitle(article.title, lang, article.slug)} ${article.excerpt}`.toLowerCase().includes(normalizedSearch);
    const matchesFilter = activeFilter === "all" || article.difficulty_level === activeFilter || article.category === activeFilter;
    return matchesSearch && matchesFilter;
  });
  const toggleCompleted = (slug) => setCompleted((items) => items.includes(slug) ? items.filter((item) => item !== slug) : [...items, slug]);
  const toggleSaved = (slug) => setSaved((items) => items.includes(slug) ? items.filter((item) => item !== slug) : [...items, slug]);
  const nextArticle = enrichedArticles.find((article) => !completed.includes(article.slug)) || enrichedArticles[0];

  if (!topic) return null;

  return (
    <section className="mt-6" dir={isRtl ? "rtl" : "ltr"}>
      <div className="rounded-[1.5rem] border border-[#BFDADC] bg-white/88 p-5 shadow-[0_14px_35px_rgba(8,22,51,0.08)] backdrop-blur md:p-7">
        <div className="grid gap-5 lg:grid-cols-[1fr_22rem] lg:items-end">
          <div>
            <p className="text-xs font-black uppercase tracking-[0.34em] text-[#168F92] md:text-sm">{copy.eyebrow}</p>
            <h2 className="mt-2 font-heading text-3xl font-black tracking-[-0.04em] text-[#081633] md:text-5xl">{copy.title}</h2>
            <p className="mt-3 max-w-2xl font-medium leading-7 text-[#10233E]">{copy.intro}</p>
          </div>
          <LearningArticleSearch value={search} onChange={setSearch} placeholder={copy.search} label={copy.searchLabel} isRtl={isRtl} />
        </div>
        <LearningTopicFilters activeFilter={activeFilter} onChange={setActiveFilter} lang={lang} />
      </div>

      <LearningPathCards articles={enrichedArticles} prefix={prefix} topicSlug={topicSlug} lang={lang} />
      <LearningProgressTools total={enrichedArticles.length} completedCount={completed.length} savedCount={saved.length} nextArticle={nextArticle} prefix={prefix} topicSlug={topicSlug} lang={lang} />

      {isLoading && <div className="mt-6 flex items-center gap-2 rounded-3xl border border-[#BFDADC] bg-white/88 p-6 font-semibold text-[#10233E]"><Loader2 className="h-5 w-5 animate-spin" /> {copy.loading}</div>}

      {!isLoading && (
        <div className="mt-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {filteredArticles.map((article, index) => (
            <PublicArticleTopicRow key={article.slug} article={article} href={localizedLearningArticleUrl({ lang, category: topicSlug, article })} lang={lang} featured={index === 0} completed={completed.includes(article.slug)} saved={saved.includes(article.slug)} onToggleComplete={toggleCompleted} onToggleSaved={toggleSaved} />
          ))}
          {filteredArticles.length === 0 && <div className="rounded-3xl border border-dashed border-[#BFDADC] bg-white/88 p-8 text-center font-semibold text-[#10233E] sm:col-span-2 xl:col-span-3">{copy.empty}</div>}
        </div>
      )}
      <MiniTopicQuiz lang={lang} />
    </section>
  );
}